import { DEFAULT_PAGE_SIZE } from '@hit-game/shared/constants';
import { useCallback, useEffect, useState } from 'react';

import type { Paginated } from '../lib/http';
import { useDebounced } from './useDebounced';

export interface PaginatedListParams {
  page: number;
  limit: number;
  search?: string;
}

export interface PaginatedList<T> {
  items: T[];
  total: number;
  totalPages: number;
  loading: boolean;
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (value: string) => void;
  /** Tải lại trang hiện tại — gọi sau khi thêm/sửa/xoá. */
  reload: () => void;
}

/**
 * Danh sách phân trang + tìm kiếm.
 *
 * Các trang Kho tài khoản / Proxy / Người dùng trước đây mỗi trang tự dựng lại
 * đúng bộ state này (page, search, loading, totalPages) kèm `useRef` chống fetch
 * lần đầu và debounce riêng — 4 bản sao lệch nhau vài chi tiết. Gom về một chỗ.
 */
export function usePaginatedList<T>(
  fetcher: (params: PaginatedListParams) => Promise<Paginated<T>>,
  options: { limit?: number } = {},
): PaginatedList<T> {
  const { limit = DEFAULT_PAGE_SIZE } = options;

  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const debouncedSearch = useDebounced(search);

  // Đổi từ khoá thì phải về trang 1 — nếu không, đang ở trang 5 mà lọc còn 2
  // trang sẽ ra danh sách rỗng và trông như "không có kết quả".
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetcher({ page, limit, search: debouncedSearch || undefined })
      .then((result) => {
        if (cancelled) return;
        setItems(result.items);
        setTotal(result.total);
        setTotalPages(Math.max(1, result.totalPages));
      })
      .catch(() => {
        // Toast đã do interceptor bắn; ở đây chỉ cần không giữ dữ liệu cũ gây hiểu nhầm.
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      // Chặn response về muộn của trang cũ ghi đè trang mới.
      cancelled = true;
    };
  }, [fetcher, page, limit, debouncedSearch, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  return {
    items,
    total,
    totalPages,
    loading,
    page,
    setPage,
    search,
    setSearch,
    reload,
  };
}
